import { Cog8ToothIcon, PlusIcon, ChevronUpIcon, XMarkIcon, UsersIcon } from '@heroicons/react/24/solid'
import { Avatar } from '@/components/catalyst-ui/avatar'
import { useState, useEffect } from 'react'
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  DropdownDivider,
} from '@/components/catalyst-ui/dropdown'
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions
} from '@/components/catalyst-ui/dialog'
import { SidebarItem } from '@/components/catalyst-ui/sidebar'
import { useSelectionStore } from '@/state/selection.store'
import { useNavigate } from 'react-router-dom'
import { useWorkspaces } from '@/features/workspace/hooks/useWorkspaces'
import { usePendingInvitations } from '@/features/team/hooks/usePendingInvitations'
import { useSharedProjectsCount } from '@/features/workspace/hooks/useSharedProjectsCount'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast'
import { Button } from '@/components/catalyst-ui/button'
import { Field, Label } from '@/components/catalyst-ui/fieldset'
import { Input } from '@/components/catalyst-ui/input'
import CreateWorkspaceDialog from '@/features/workspace/components/CreateWorkspaceDialog'
import { safeLocalStorage } from '@/lib/safe-storage'

// Mock user fallback

// function getUserFromToken() {
//   try {
//     const stored = localStorage.getItem('user_data')
//     if (stored) return JSON.parse(stored)
//     const token = localStorage.getItem('auth_token')
//     if (!token) return null
//     const [, payload] = token.split('.')
//     const json = JSON.parse(atob(payload))
//     return {
//       id: json.id || json._id,
//       name: json.name || 'User',
//       email: json.email || '',
//       avatar: json.avatar || null,
//     }
//   } catch {
//     return null
//   }
// }



/**
 * @param {object} [props]
 * @param {(workspaceId: string) => void} [props.onWorkspaceSwitch]
 *   Optional override for what happens after picking a workspace from
 *   the dropdown. Default behaviour (used by the sidebar) navigates to
 *   `/projects?workspace=<id>`. The floating variant on `/templates` /
 *   `/pricing` overrides this to stay on the current page and just swap
 *   the workspace query param.
 */
export function UserAccountMenu({ onWorkspaceSwitch } = {}) {
  const activeWorkspaceId = useSelectionStore(state => state.activeWorkspaceId)
  const switchWorkspace = useSelectionStore(state => state.switchWorkspace)
  const { workspaces, loading, createWorkspace } = useWorkspaces()
  const { invitations: pendingInvitations } = usePendingInvitations()
  const { count: sharedProjectsCount } = useSharedProjectsCount()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [newWorkspaceName, setNewWorkspaceName] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const navigate = useNavigate()
  // const currentUser = getUserFromToken() || mockUser

  // Check if user has pending invitations
  const hasPendingInvitations = pendingInvitations.length > 0

  // Ensure a default workspace is selected
  useEffect(() => {
    if (!activeWorkspaceId && Array.isArray(workspaces) && workspaces.length > 0) {
      const firstId = workspaces[0].id || workspaces[0]._id
      if (firstId) {
        switchWorkspace(firstId)
      }
    }
  }, [activeWorkspaceId, workspaces, switchWorkspace])

  const currentWorkspace = Array.isArray(workspaces)
    ? workspaces.find(ws => (ws.id || ws._id) === activeWorkspaceId) || workspaces[0]
    : null
  const currentWorkspaceName = currentWorkspace?.name || 'Workspace'
  const currentWorkspaceLogo = currentWorkspace?.branding?.logo || currentWorkspace?.logo || null

  const handleWorkspaceSwitch = workspaceId => {
    switchWorkspace(workspaceId)
    if (typeof onWorkspaceSwitch === 'function') {
      onWorkspaceSwitch(workspaceId)
      return
    }
    navigate(`/projects?workspace=${workspaceId}`, { replace: true })
  }

  const handleSignOut = () => {
    // Clear auth data and redirect to login
    safeLocalStorage.removeItem('auth_token')
    safeLocalStorage.removeItem('user_data')
    window.location.href = '/login'
  }

  const handleCreateWorkspace = async (e) => {
    e.preventDefault()

    if (!newWorkspaceName.trim()) {
      showSimpleError('Please enter a workspace name')
      return
    }

    setIsCreating(true)

    try {
      const response = await createWorkspace(newWorkspaceName.trim())
      const newWorkspace = response?.data || response
      const workspaceName = newWorkspace?.name || newWorkspaceName.trim()
      showSimpleSuccess(`Workspace "${workspaceName}" created successfully`)
      setIsCreateModalOpen(false)
      setNewWorkspaceName('')

      // Switch to the new workspace
      const newId = newWorkspace?.id || newWorkspace?._id
      if (newId) {
        switchWorkspace(newId)
        navigate(`/projects?workspace=${newId}`, { replace: true })
      }
    } catch (error) {
      showSimpleError('Failed to create workspace')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dropdown>
      {({ open }) => (
        <>
          <DropdownButton as={SidebarItem} className="data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2">
            <div className="flex min-w-0 items-center justify-between gap-3">
              <div className="relative">
                <Avatar
                  src={currentWorkspaceLogo || null}
                  initials={String(currentWorkspaceName).trim().charAt(0).toUpperCase()}
                  alt={currentWorkspaceName}
                  className="size-8 bg-black text-white border-2 border-[#E4E4E7]"
                />
                {hasPendingInvitations && (
                  <div className="absolute -top-[1px] -right-[1px] h-2.5 w-2.5 rounded-full bg-[#6D28D9]" />
                )}
              </div>
              <div data-sidebar-label className="min-w-0">
                <div className="block truncate text-sm/5 font-medium text-[#71717A] dark:text-white">
                  {currentWorkspaceName}
                </div>
              </div>
            </div>
            <ChevronUpIcon data-sidebar-trailing className={`ml-auto shrink-0 text-[#71717A] transition-transform duration-200 ${open ? '' : 'rotate-90'}`} />
          </DropdownButton>

          <DropdownMenu
            anchor="top start"
            // Catalyst's DropdownMenu sets grid-template-columns inside
            // a `@supports (grid-template-columns: subgrid)` rule —
            // Tailwind arbitrary classes can't beat that consistently
            // because of @layer ordering, so the 5 reserved columns
            // (auto / 1fr / 1.5rem / 0.5rem / auto) keep applying and
            // the scrollable workspace list ends up sitting in col 1
            // (`auto`, sized to natural content) instead of spanning
            // the menu. That's the "dead space after the scrollbar"
            // and why `!justify-between` items push their trailing
            // content (the "22" badge) to the menu's right edge instead
            // of the item's right edge. Inline style wins over the
            // @supports class rule — force a single full-width column.
            style={{ gridTemplateColumns: '1fr' }}
            className="!w-[260px] border border-gray-200"
          >
            {/* Workspace List */}
            {loading ? (
              <DropdownItem disabled>
                <DropdownLabel>Loading workspaces...</DropdownLabel>
              </DropdownItem>
            ) : Array.isArray(workspaces) && workspaces.length > 0 ? (
              <div className="max-h-40 overflow-y-auto cd-scrollbar">
                {workspaces.map((ws) => {
                  const workspaceId = ws._id || ws.id
                  const isActive = workspaceId === activeWorkspaceId
                  return (
                    <DropdownItem
                      key={workspaceId}
                      onClick={() => handleWorkspaceSwitch(workspaceId)}
                      className={`${isActive ? 'bg-zinc-100 dark:bg-zinc-800' : ''} !flex !w-full`}
                    >
                      <div className="mr-3">
                        <Avatar
                          src={ws.branding?.logo || ws.logo || null}
                          initials={String(ws.name || 'W').trim().charAt(0).toUpperCase()}
                          alt={ws.name || 'Workspace'}
                          className="size-7 bg-black text-white border-2 border-[#E4E4E7]"
                        />
                      </div>
                      <div className="flex flex-1 items-center min-w-0">
                        <div className="flex flex-col">
                          <DropdownLabel className="truncate max-w-[11rem]">
                            {ws.name || 'workspace'}
                          </DropdownLabel>
                        </div>
                      </div>
                    </DropdownItem>
                  )
                })}
              </div>
            ) : (
              <DropdownItem onClick={() => setIsCreateModalOpen(true)} className="group">
                <PlusIcon className="!w-5 !h-5 !ml-1 !text-[#71717A] group-hover:!text-[#09090B]" />
                <DropdownLabel>Create your first workspace</DropdownLabel>
              </DropdownItem>
            )}

            <DropdownDivider />
            <DropdownItem to={`/shared-projects?workspace=${activeWorkspaceId || ''}`} className="group !flex !w-full gap-4 !justify-between">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <UsersIcon className="!w-5 !h-5 !ml-1 !text-[#71717A] group-hover:!text-[#09090B]" />
                </div>
                <DropdownLabel>Shared Websites</DropdownLabel>
              </div>
              <span className="text-sm text-[#71717A] group-hover:!text-[#09090B] ml-auto">
                {sharedProjectsCount}
              </span>
            </DropdownItem >
            <DropdownItem to={`/settings/workspace-management?workspace=${activeWorkspaceId || ''}`} className="group !flex !w-full gap-4">
              <div className="relative">
                <Cog8ToothIcon className="!w-5 !h-5 !ml-1 !text-[#71717A] group-hover:!text-[#09090B]" />
                {hasPendingInvitations && (
                  <div className="absolute top-[1px] right-[15px] h-2.5 w-2.5 rounded-full bg-[#6D28D9]" />
                )}
              </div>
              <DropdownLabel>Manage workspace</DropdownLabel>
            </DropdownItem >
            <DropdownItem onClick={() => setIsCreateModalOpen(true)} className="group !flex !w-full gap-2">
              <PlusIcon className="!w-5 !h-5 !ml-1 !text-[#71717A] group-hover:!text-[#09090B]" />
              <DropdownLabel>New workspace</DropdownLabel>
            </DropdownItem>

            {/* Commented out other menu items */}
            {/* <DropdownItem to="/account">
          <UserCircleIcon />
          <DropdownLabel>My Profile</DropdownLabel>
        </DropdownItem>
        <DropdownItem to="/help">
          <LightBulbIcon />
          <DropdownLabel>Help &amp; Support</DropdownLabel>
        </DropdownItem>
        <DropdownItem to="/updates">
          <NewspaperIcon />
          <DropdownLabel>Updates</DropdownLabel>
        </DropdownItem>
        <DropdownItem onClick={handleSignOut}>
          <ArrowRightStartOnRectangleIcon />
          <DropdownLabel>Sign out</DropdownLabel>
        </DropdownItem> */}
          </DropdownMenu>

          {/* Create Workspace Modal */}
          <CreateWorkspaceDialog
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            workspaceName={newWorkspaceName}
            onWorkspaceNameChange={setNewWorkspaceName}
            onCreate={handleCreateWorkspace}
            isCreating={isCreating}
          />
        </>
      )}
    </Dropdown>
  )
}
